import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { decodeIdentityToken, getIdentityToken } from 'lib';
import { refreshToken as refreshIdentityToken, signOut } from 'services';
import { PATHNAME } from 'utils';

const {
  LOCAL: {
    LOGIN: { BASE_URL: LOGIN_BASE_URL },
  },
} = PATHNAME;

type RefreshTokenOption = {
  redirectedPathname?: string;
  refreshThreshold?: number;
  initialCheckDelay?: number;
  healthCheckInterval?: number;
  onRefresh?: () => void;
  onSignOut?: () => void;
  onError?: (error: Error, operation: string) => void;
};

const MIN_REFRESH_THRESHOLD = 0.1;
const MAX_REFRESH_THRESHOLD = 0.9;
const DEFAULT_REFRESH_THRESHOLD = 0.75;
const DEFAULT_INITIAL_CHECK_DELAY = 100;
const DEFAULT_HEALTH_CHECK_INTERVAL = 2 * 60 * 1000;

const useRefreshToken = ({
  redirectedPathname = LOGIN_BASE_URL,
  refreshThreshold = DEFAULT_REFRESH_THRESHOLD,
  initialCheckDelay = DEFAULT_INITIAL_CHECK_DELAY,
  healthCheckInterval = DEFAULT_HEALTH_CHECK_INTERVAL,
  onRefresh,
  onSignOut,
  onError,
}: RefreshTokenOption = {}) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const intervalRef = useRef<NodeJS.Timeout>();
  const isMountedRef = useRef(true);
  const isRefreshingRef = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const validatedRefreshThreshold = useMemo(
    () =>
      Math.max(
        MIN_REFRESH_THRESHOLD,
        Math.min(refreshThreshold, MAX_REFRESH_THRESHOLD),
      ),
    [refreshThreshold],
  );
  const validatedInitialCheckDelay = useMemo(
    () => Math.max(0, initialCheckDelay),
    [initialCheckDelay],
  );

  const cleanupTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const handleAuthError = useCallback(
    (error: unknown, operation: string) => {
      if (!isMountedRef.current) return;

      const { message = 'خطای غیرمنتظره رخ داده است.' } =
        error instanceof Error ? error : {};
      console.error(`${operation}:`, error);
      onError?.(new Error(message), operation);
    },
    [onError],
  );

  const performSignOut = useCallback(async () => {
    if (!isMountedRef.current) return;

    cleanupTimers();

    try {
      await signOut();
      // Only redirect if we're not already on the target page
      if (pathname !== redirectedPathname) {
        navigate(redirectedPathname);
      }
      onSignOut?.();
    } catch (error) {
      handleAuthError(error, 'Session termination failed');
    }
  }, [
    navigate,
    pathname,
    redirectedPathname,
    onSignOut,
    cleanupTimers,
    handleAuthError,
  ]);

  const refreshAuthToken = useCallback(async () => {
    if (!isMountedRef.current || isRefreshingRef.current) return;

    // Don't refresh if we're on the target page
    if (pathname === redirectedPathname) return;

    try {
      const [
        { refreshToken: initialRefreshToken },
        { decodedAccessToken, decodedRefreshToken },
      ] = await Promise.all([getIdentityToken(), decodeIdentityToken()]);

      if (!initialRefreshToken || !decodedAccessToken || !decodedRefreshToken) {
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const accessTokenExp = Number(decodedAccessToken.exp);
      const accessTokenIat = Number(decodedAccessToken.iat);
      const refreshTokenExp = Number(decodedRefreshToken.exp);

      // Check if token is expired
      if (currentTime >= refreshTokenExp) {
        await performSignOut();
        return;
      }

      const accessTokenLifetime = accessTokenExp - accessTokenIat;

      // Invalid token lifetime, skip refresh logic
      if (accessTokenLifetime <= 0) {
        console.warn('Invalid token lifetime, skipping token refresh.');
        return;
      }

      // Calculate token lifetime progress
      const timeElapsed = Math.abs(currentTime - accessTokenIat);
      const tokenLifetimeProgress = timeElapsed / accessTokenLifetime;

      if (tokenLifetimeProgress >= validatedRefreshThreshold) {
        isRefreshingRef.current = true;

        await refreshIdentityToken({ refreshToken: initialRefreshToken });
        onRefresh?.();
      }
    } catch (error) {
      handleAuthError(error, 'Token refresh failed');
    } finally {
      if (isMountedRef.current) {
        isRefreshingRef.current = false;
      }
    }
  }, [
    pathname,
    validatedRefreshThreshold,
    redirectedPathname,
    onRefresh,
    performSignOut,
    handleAuthError,
  ]);

  // Initial setup and interval
  useEffect(() => {
    isMountedRef.current = true;

    // Initial check with a small delay to avoid blocking initial render
    const initialCheckTimeout = setTimeout(
      refreshAuthToken,
      validatedInitialCheckDelay,
    );

    // Set up periodic checks
    intervalRef.current = setInterval(refreshAuthToken, healthCheckInterval);

    return () => {
      isMountedRef.current = false;
      isRefreshingRef.current = false;
      clearTimeout(initialCheckTimeout);
      cleanupTimers();
    };
  }, [
    healthCheckInterval,
    validatedInitialCheckDelay,
    refreshAuthToken,
    cleanupTimers,
  ]);

  return {
    refreshToken: refreshAuthToken,
    signOut: performSignOut,
  };
};

export { useRefreshToken };
